// Relative paths, not the "@assets" alias -- same reasoning as flameBadgeArt.ts and rewardArtwork.ts:
// "@assets/*" isn't in jest's moduleNameMapper, and this module's test needs the real imports. One
// static import per file (never a dynamic `require`) so webpack fingerprints every asset.
import achGiveFirst from "../../../../assets/images/achievementBadges/ach-give-first.png";
import achGive10 from "../../../../assets/images/achievementBadges/ach-give-10.png";
import achGive50 from "../../../../assets/images/achievementBadges/ach-give-50.png";
import achGive100 from "../../../../assets/images/achievementBadges/ach-give-100.png";
import achGive250 from "../../../../assets/images/achievementBadges/ach-give-250.png";
import achGive500 from "../../../../assets/images/achievementBadges/ach-give-500.png";
import achGiveHypeSquad from "../../../../assets/images/achievementBadges/ach-give-hype-squad.png";
import achGiveDistinct25 from "../../../../assets/images/achievementBadges/ach-give-distinct-25.png";
import achGiveDistinct50 from "../../../../assets/images/achievementBadges/ach-give-distinct-50.png";
import achGiveAllValues from "../../../../assets/images/achievementBadges/ach-give-all-values.png";
import achRecvRoundRobin from "../../../../assets/images/achievementBadges/ach-recv-round-robin.png";
import achRecvDistinct25 from "../../../../assets/images/achievementBadges/ach-recv-distinct-25.png";
import achRecvDistinct50 from "../../../../assets/images/achievementBadges/ach-recv-distinct-50.png";
import achRecvAllValues from "../../../../assets/images/achievementBadges/ach-recv-all-values.png";
import achRecvEveryTeam from "../../../../assets/images/achievementBadges/ach-recv-every-team.png";
import achBurstGive5Day from "../../../../assets/images/achievementBadges/ach-burst-give-5-day.png";
import achBurstGive10Week from "../../../../assets/images/achievementBadges/ach-burst-give-10-week.png";
import achBurstRecv5Day from "../../../../assets/images/achievementBadges/ach-burst-recv-5-day.png";
import achBurstRecv250Week from "../../../../assets/images/achievementBadges/ach-burst-recv-250-week.png";
import achBurstMaxScore from "../../../../assets/images/achievementBadges/ach-burst-max-score.png";
import achConsistRecv4w from "../../../../assets/images/achievementBadges/ach-consist-recv-4w.png";
import achConsistRecv12w from "../../../../assets/images/achievementBadges/ach-consist-recv-12w.png";
import achConsistRecv26w from "../../../../assets/images/achievementBadges/ach-consist-recv-26w.png";
import achConsistRecv52w from "../../../../assets/images/achievementBadges/ach-consist-recv-52w.png";
import achConsistGive4w from "../../../../assets/images/achievementBadges/ach-consist-give-4w.png";
import achConsistGive12w from "../../../../assets/images/achievementBadges/ach-consist-give-12w.png";
import achConsistGive26w from "../../../../assets/images/achievementBadges/ach-consist-give-26w.png";

/**
 * Bundled achievement badge artwork (176x176 PNG, transparent), keyed by the api's own EXACT
 * `badge_definitions.code` (api `migrations/20260807090300_Insert-BadgeDefinitions-Achievements.js`).
 * Keyed on the code verbatim, like `flameBadgeArt.ts`: achievement codes are static strings, identical in
 * every environment. File names are the code with `_` turned into `-`.
 *
 * Covers the ten GIVING achievements (Figma `Achievements_L`, `9655:1282`), the five RECEIVING ones
 * (`Achievements_A_L`, `9655:1274`), the five BURST ones (`Burst_L`, `9669:1327`), and the seven
 * CONSISTENCY ones (`Consustency_L` [sic], `9670:1374`). With these, every achievement has art.
 * Code-to-art pairing was checked
 * against the art, not only the Figma layer names: the give-count ladder escalates paper planes into
 * rockets, the distinct-recipient ladder grows from two people to a crowd, the distinct-giver
 * ladder from one heart to three, the receiving-streak ladder shows a calendar with an increasing
 * number of filled dots (teal for 4/12 weeks, blue for 26/52), and the giving-streak ladder shows a
 * hand holding one, two, then three hearts (purple, gold, pink).
 *
 * ⚠️ Deliberately NOT as labelled in Figma: the board puts the people icon on Complete Player and the
 * sparkles on Company Wide. Swapped here (pavel, 2026-09-23) so each icon means the same thing in both
 * rows -- sparkles is "every core value" (Well Rounded / Complete Player), people is "many people"
 * (Hype Squad..Ambassador / Company Wide). Do not "correct" this back to the Figma layer names.
 */
const ACHIEVEMENT_BADGE_ART: Record<string, string> = {
  ach_give_first: achGiveFirst,
  ach_give_10: achGive10,
  ach_give_50: achGive50,
  ach_give_100: achGive100,
  ach_give_250: achGive250,
  ach_give_500: achGive500,
  ach_give_hype_squad: achGiveHypeSquad,
  ach_give_distinct_25: achGiveDistinct25,
  ach_give_distinct_50: achGiveDistinct50,
  ach_give_all_values: achGiveAllValues,
  ach_recv_round_robin: achRecvRoundRobin,
  ach_recv_distinct_25: achRecvDistinct25,
  ach_recv_distinct_50: achRecvDistinct50,
  ach_recv_all_values: achRecvAllValues,
  ach_recv_every_team: achRecvEveryTeam,
  ach_burst_give_5_day: achBurstGive5Day,
  ach_burst_give_10_week: achBurstGive10Week,
  ach_burst_recv_5_day: achBurstRecv5Day,
  ach_burst_recv_250_week: achBurstRecv250Week,
  ach_burst_max_score: achBurstMaxScore,
  ach_consist_recv_4w: achConsistRecv4w,
  ach_consist_recv_12w: achConsistRecv12w,
  ach_consist_recv_26w: achConsistRecv26w,
  ach_consist_recv_52w: achConsistRecv52w,
  ach_consist_give_4w: achConsistGive4w,
  ach_consist_give_12w: achConsistGive12w,
  ach_consist_give_26w: achConsistGive26w,
};

/**
 * Resolves an achievement badge's `code` to its bundled artwork, or `null` for any code without art
 * (every other category, and any achievement code not in the map above). Never throws. `hasOwnProperty` for
 * the same reason as `resolveFlameBadgeArt`: a bare index would hand back inherited `Object.prototype`
 * members for codes like `"constructor"`.
 *
 * @param code - a badge's own `code`, e.g. `"ach_give_first"`.
 * @returns the bundled asset URL, or `null`.
 */
export const resolveAchievementBadgeArt = (code: string): string | null =>
  Object.prototype.hasOwnProperty.call(ACHIEVEMENT_BADGE_ART, code) ? ACHIEVEMENT_BADGE_ART[code] : null;
